/*
 * The inbox's writes and what they say (frontend performance plan, W5, F05, F07, F08, F12). Each is
 * a mutation through the page's query client, so a refused write also reaches the client's own
 * failure handling (it reads the workspace list again, which drops an offboarded workspace), and:
 * - it is acknowledged when its own answer lands, never after the reads it makes stale;
 * - it reads again only what it made stale (`effects.ts`), for the conversations it touched;
 * - the same action on the same rows while the first is still running is one action, not two;
 * - a refusal says nothing changed; a failure whose outcome is unknown says it could not confirm,
 *   and the reads show what is true (`write-outcome.ts`);
 * - a batch that partly fails says what was done and offers to try again only what was not.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { appWeekdayTime } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import {
  type InboxAssignee,
  InboxAssignmentChangedSchema,
  type InboxConversation,
  type InboxDoneResult,
} from "../../src/inbox-v2/contract.ts";
import { InboxApiError, inboxApi } from "./api.ts";
import { doneToast, mergeDone } from "./done-toast.ts";
import { type InboxChange, refreshAfter } from "./effects.ts";
import type { Toast } from "./use-inbox.ts";
import { failedWriteText, writeOutcome } from "./write-outcome.ts";

const conversations = (n: number) =>
  `${n.toLocaleString("en-GB")} conversation${n === 1 ? "" : "s"}`;

/** A successful assignment leaves the shared snooze alone; say so only for sleeping rows. */
function snoozedAssignmentNote(rows: readonly InboxConversation[]): string {
  const snoozed = rows.filter((row) => row.snooze != null);
  const until = snoozed[0]?.snooze?.until;
  if (!until) return "";
  return rows.length === 1
    ? ` Still snoozed until ${appWeekdayTime(until)}.`
    : ` ${snoozed.length} of them stay snoozed.`;
}

type Previous = readonly { planId: string; value: boolean }[];

/** Undo for a bulk write: each conversation back to what it was, in at most two calls. */
function undoTo<T>(previous: Previous, apply: (ids: string[], value: boolean) => Promise<T>) {
  return async (): Promise<T[]> => {
    const answers: T[] = [];
    for (const value of [true, false]) {
      const ids = previous.filter((row) => row.value === value).map((row) => row.planId);
      if (ids.length) answers.push(await apply(ids, value));
    }
    return answers;
  };
}

type Confirmation = "confirmed" | "kept" | "unresolved";

/** One unsure label sent again as the rep's word; a refusal keeps it, an unknown end is unresolved. */
async function confirmOne(workspaceId: string, row: InboxConversation): Promise<Confirmation> {
  if (!row.label || !row.leadMessageId) return "kept";
  try {
    const refused = await inboxApi.relabel(
      workspaceId,
      [{ planId: row.id, messageId: row.leadMessageId }],
      row.label.code,
    );
    return refused.length ? "kept" : "confirmed";
  } catch (error) {
    return writeOutcome(error).kind === "refused" ? "kept" : "unresolved";
  }
}

type Job = {
  readonly change: InboxChange;
  readonly planIds: readonly string[];
  readonly run: () => Promise<unknown>;
};

/** What a failed write is called in its sentence: "Could not mark them done" / "the change". */
type Words = { readonly verb: string; readonly noun: string };

/**
 * One write's lifecycle: once per gesture, through a mutation, the stale reads refreshed after it
 * settles whatever its end, and its failure said in words true to how it ended. Resolves to whether
 * it was committed; `quiet` leaves saying a failure to the caller.
 */
export function useInboxWrite(workspaceId: string, onToast: (toast: Toast) => void) {
  const client = useQueryClient();
  const running = useRef(new Set<string>());
  const mutation = useMutation({
    mutationFn: (job: Job) => job.run(),
    // Not awaited: the write is acknowledged on its own answer, the reads follow (F12).
    onSettled: (_data, _error, job) => {
      void refreshAfter(client, workspaceId, job.change, job.planIds);
    },
  });
  const { mutateAsync } = mutation;
  const write = useCallback(
    async (
      change: InboxChange,
      planIds: readonly string[],
      words: Words,
      run: () => Promise<unknown>,
      quiet = false,
    ): Promise<boolean> => {
      const gesture = `${change}:${[...planIds].sort().join(",")}`;
      if (running.current.has(gesture)) return false;
      running.current.add(gesture);
      try {
        await mutateAsync({ change, planIds, run });
        return true;
      } catch (error) {
        if (!quiet) onToast({ text: failedWriteText(words, writeOutcome(error)), undo: null });
        return false;
      } finally {
        running.current.delete(gesture);
      }
    },
    [mutateAsync, onToast],
  );
  return { write, busy: mutation.isPending };
}

type Write = ReturnType<typeof useInboxWrite>["write"];

/**
 * The rep's word on whether the newest lead message asks for a meeting (LEA-494), which wins over
 * the AI's: the counts and the list are read again, so the thread moves between Meeting requests
 * and Needs reply at once. A yes on a thread with another label also labels it Interested.
 */
function meetingMark(workspaceId: string, write: Write, onToast: (toast: Toast) => void) {
  return async (row: InboxConversation, value: boolean) => {
    const messageId = row.leadMessageId;
    if (!messageId) return;
    let relabelled = false;
    const words = {
      verb: value ? "mark it a meeting request" : "unmark the meeting request",
      noun: "meeting mark",
    };
    const committed = await write("meeting", [row.id], words, async () => {
      relabelled = (await inboxApi.meeting(workspaceId, row.id, messageId, value)).changed;
    });
    if (!committed) return;
    onToast({
      text: value
        ? relabelled
          ? "Marked as a meeting request and labelled Interested."
          : "Marked as a meeting request."
        : "No longer a meeting request.",
      undo: null,
    });
  };
}

export function useInboxActions(workspaceId: string, onToast: (toast: Toast) => void) {
  const client = useQueryClient();
  const { write, busy } = useInboxWrite(workspaceId, onToast);
  const ids = (rows: readonly InboxConversation[]) => rows.map((row) => row.id);
  /** An Undo through the same lifecycle; resolves whether it was committed. */
  const undoing = (change: InboxChange, planIds: string[], run: () => Promise<unknown>) => () =>
    write(change, planIds, { verb: "undo it", noun: "undo" }, run);

  const done = async (rows: readonly InboxConversation[]) => {
    if (!rows.length) return false;
    const putAway = rows.some((row) => row.doneAt === null);
    const apply = (planIds: string[], value: boolean) => inboxApi.done(workspaceId, planIds, value);
    let result: InboxDoneResult = { previous: [] };
    const words = { verb: putAway ? "mark them done" : "put them back", noun: "change" };
    const committed = await write("done", ids(rows), words, async () => {
      result = await apply(ids(rows), putAway);
    });
    if (!committed) return false;
    // Done also closes the agent's draft and going back reopens it (LEA-225), so Undo says what it
    // brought back, including a draft a newer lead message has replaced since.
    const back = undoTo(result.previous, apply);
    onToast({
      text: doneToast(rows.length, putAway, result),
      undo: async () => {
        let answers: InboxDoneResult[] = [];
        const undone = await undoing("done", ids(rows), async () => {
          answers = await back();
        })();
        return undone ? doneToast(rows.length, !putAway, mergeDone(answers)) : undefined;
      },
    });
    return putAway;
  };

  const seen = async (rows: readonly InboxConversation[], value?: boolean) => {
    if (!rows.length) return;
    const next = value ?? rows.some((row) => row.unread);
    const apply = (planIds: string[], to: boolean) => inboxApi.seen(workspaceId, planIds, to);
    let previous: Previous = [];
    const words = { verb: `mark them ${next ? "seen" : "unseen"}`, noun: "change" };
    const committed = await write("seen", ids(rows), words, async () => {
      previous = await apply(ids(rows), next);
    });
    if (!committed) return;
    onToast({
      text: `${conversations(rows.length)} marked ${next ? "seen" : "unseen"}.`,
      undo: undoing("seen", ids(rows), undoTo(previous, apply)),
    });
  };

  const relabel = async (label: InboxLabelCode | null, rows: readonly InboxConversation[]) => {
    const items = rows.flatMap((row) =>
      row.leadMessageId ? [{ planId: row.id, messageId: row.leadMessageId }] : [],
    );
    if (!items.length) return;
    let refused: string[] = [];
    const words = { verb: "relabel them", noun: "relabel" };
    const committed = await write(
      "label",
      items.map((item) => item.planId),
      words,
      async () => {
        refused = await inboxApi.relabel(workspaceId, items, label);
      },
    );
    if (!committed) return;
    const changed = items.length - refused.length;
    onToast({
      text: refused.length
        ? `${conversations(changed)} relabelled; ${refused.length} had a newer reply and kept their label.`
        : `${conversations(changed)} relabelled.`,
      undo: null,
    });
  };

  const meeting = meetingMark(workspaceId, write, onToast);

  /**
   * Assign rows to a person, or to nobody. From the reading pane `expectedRevision` is what the pane
   * showed: someone else's reassignment since is said, nothing is changed, and the pane reads again.
   */
  const assign = async (
    rows: readonly InboxConversation[],
    assignee: InboxAssignee | null,
    expectedRevision?: number,
  ) => {
    if (!rows.length) return;
    let changedBy: InboxAssignee | null | undefined;
    const words = { verb: rows.length === 1 ? "assign it" : "assign them", noun: "assignment" };
    const committed = await write("assign", ids(rows), words, async () => {
      try {
        await inboxApi.assign(workspaceId, ids(rows), assignee?.userId ?? null, expectedRevision);
      } catch (error) {
        if (!(error instanceof InboxApiError)) throw error;
        const changed = InboxAssignmentChangedSchema.safeParse(error.body);
        if (!changed.success) throw error;
        changedBy = changed.data.assignee;
      }
    });
    if (!committed) return;
    if (changedBy !== undefined) {
      onToast({
        text: `Someone reassigned it meanwhile, to ${changedBy ? changedBy.name : "nobody"}. Nothing changed; choose again.`,
        undo: null,
      });
      return;
    }
    onToast({
      text:
        (assignee
          ? `${conversations(rows.length)} assigned to ${assignee.name}.`
          : `${conversations(rows.length)} unassigned.`) + snoozedAssignmentNote(rows),
      undo: null,
    });
  };

  /**
   * Settle labels Jev was unsure of: the same label, sent again as the rep's word on it, one
   * conversation at a time. What was confirmed stays confirmed when a later one fails: the toast
   * says each part, and Try again sends only the ones not known to have been confirmed (F08).
   */
  const confirmLabels = async (rows: readonly InboxConversation[]): Promise<void> => {
    const unsure = rows.filter((row) => row.label?.uncertain && row.leadMessageId);
    if (!unsure.length) return;
    const confirmed: string[] = [];
    const kept: string[] = [];
    const unresolved: InboxConversation[] = [];
    await write("label", ids(unsure), { verb: "confirm them", noun: "confirmation" }, async () => {
      for (const row of unsure) {
        const outcome = await confirmOne(workspaceId, row);
        if (outcome === "unresolved") unresolved.push(row);
        else (outcome === "kept" ? kept : confirmed).push(row.id);
      }
    });
    const parts = [
      `${conversations(confirmed.length)} confirmed.`,
      kept.length ? `${conversations(kept.length)} kept their label.` : null,
      unresolved.length ? `Could not confirm ${conversations(unresolved.length)}.` : null,
    ];
    onToast({
      text: parts.filter(Boolean).join(" "),
      undo: null,
      ...(unresolved.length
        ? { action: { label: "Try again", run: () => void confirmLabels(unresolved) } }
        : {}),
    });
  };

  /**
   * Opening an unread conversation marks it seen; a failure says so and offers to try again,
   * once, by the rep's own press: nothing retries it by itself, so it cannot loop (F07).
   */
  const markSeen = async (row: InboxConversation): Promise<boolean> => {
    if (!row.unread) return true;
    return write(
      "seen",
      [row.id],
      { verb: "mark it seen", noun: "mark as seen" },
      async () => {
        await inboxApi.seen(workspaceId, [row.id], true);
      },
      true,
    );
  };

  /** Reads again what any change can have made stale, for a caller that cannot name it. */
  const refresh = useCallback(
    () => refreshAfter(client, workspaceId, "arrival", null),
    [client, workspaceId],
  );
  return {
    done,
    seen,
    relabel,
    meeting,
    assign,
    confirm: confirmLabels,
    markSeen,
    refresh,
    busy,
    write,
  };
}
